import React, { useState, useEffect, useRef } from 'react';
import { flushSync } from 'react-dom';
import { Allotment } from "allotment";
import AddIcon from '@mui/icons-material/Add';
import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Menu,
  MenuItem,
  Avatar,
  Button,
  Badge,
  Box,
  Select,
  Divider,
  Drawer,
  TextField,
  InputLabel,
  FormControl,
  FormControlLabel,
  Checkbox,
  InputAdornment,
  Paper, Tabs,
} from '@mui/material';

import "allotment/dist/style.css";
import { LoggerUtility } from "./ComponentsLibrary";

const COLLAPSED_SIZE = 70;

const ToggleSplitPane = ({
  leftPaneVisibility = true,
  leftPaneTitle = 'Title',
  leftPaneContent = '',
  rightPaneVisibility = true,
  rightPaneTitle = 'Title',
  rightPaneContent = '',
  leftPaneSize = '50%',
  split = 'vertical',
  minSize = 200,
  classNameSplitPane = 'split-pane mt-4',
}) => {

  const errorFormName = "Toggle Split Pane";

  const allotmentRef = useRef(null);
  const containerRef = useRef(null);

  const [collapsedPane, setCollapsedPane] = useState(null);
  const [lastLeftPaneWidth, setLastLeftPaneWidth] = useState(leftPaneSize);


  const isDraggingRef = useRef(false);

  const getContainerSize = () => {
    if (containerRef.current) {
      return split === 'horizontal'
        ? containerRef.current.offsetHeight
        : containerRef.current.offsetWidth;
    }
    return window.innerWidth;
  };

  const resizeTo = (leftPx) => {
    if (!allotmentRef.current) return;
    const total = getContainerSize();
    allotmentRef.current.resize([leftPx, total - leftPx]);
  };

  const collapseLeftPane = () => {
    try {
      if (collapsedPane === 'left-Pane') {
        const total = getContainerSize();
        const leftPx = (parseFloat(lastLeftPaneWidth) / 100) * total;
        resizeTo(leftPx);
        setCollapsedPane(null);
      } else {
        resizeTo(COLLAPSED_SIZE);
        setCollapsedPane('left-Pane');
      }
    } catch (error) {
      LoggerUtility.ManageErrorLogging(error, errorFormName, "collapseLeftPane");
    }
  };

  const collapseRightPane = () => {
    try {
      if (collapsedPane === 'right-Pane') {
        const total = getContainerSize();
        const leftPx = (parseFloat(lastLeftPaneWidth) / 100) * total;
        resizeTo(leftPx);
        setCollapsedPane(null);
      } else {
        const total = getContainerSize();
        resizeTo(total - COLLAPSED_SIZE);
        setCollapsedPane('right-Pane');
      }
    } catch (error) {
      LoggerUtility.ManageErrorLogging(error, errorFormName, "collapseRightPane");
    }
  };

  const handleDragEnd = (sizes) => {
    try {
      const newSize = sizes[0];
      const total = getContainerSize();
      if (!newSize || total === 0) return;
      const pct = (newSize / total) * 100;

      if (pct < 10) {
        resizeTo(COLLAPSED_SIZE);
        setCollapsedPane('left-Pane');
      } else if (pct > 90) {
        resizeTo(total - COLLAPSED_SIZE);
        setCollapsedPane('right-Pane');
      } else {
        setCollapsedPane(null);
        setLastLeftPaneWidth(pct.toFixed(2) + '%');
      }
    } catch (error) {
      LoggerUtility.ManageErrorLogging(error, errorFormName, "handleDragEnd");
    }
  };

  // Trigger resize event so child components (e.g. PDF viewer) reflow after collapse state changes
  useEffect(() => {
    const timeout = setTimeout(() => {
      window.dispatchEvent(new Event("resize"));
    }, 100);
    return () => clearTimeout(timeout);
  }, [collapsedPane]);

  return (
    <div className="main-content">
      <div
        ref={containerRef}
        className={classNameSplitPane}
        style={{ height: 'calc(100vh - 64px)', position: 'absolute', width: '100%' }}
      >
        <Allotment
          ref={allotmentRef}
          vertical={split === 'horizontal'}
          onDragEnd={handleDragEnd}
          style={{ height: '100%', width: '100%' }}
        >
          {/* Left Pane — always mounted, content is never unmounted */}
          <Allotment.Pane minSize={COLLAPSED_SIZE} preferredSize={leftPaneSize}>
            <div className="flex-main-frame" style={{ visibility: leftPaneVisibility ? 'visible' : 'hidden' }}>
              <div className={collapsedPane === 'left-Pane' ? "frame-viewheader collapsed" : "frame-viewheader"}>
                <div className="btn-label">
                  <div className="expand-collapse">
                    <button className="icon-button" style={{ visibility: rightPaneVisibility ? 'visible' : 'hidden' }}
                      onClick={collapsedPane === 'left-Pane' ? (e) => { e.stopPropagation(); collapseLeftPane(); } : () => collapseLeftPane()}
                    >
                      <span className="fa-stack custom-icon">
                        <i className="fas fa-circle fa-stack-2x circle"></i>
                        <i className={collapsedPane === 'left-Pane' ? "fas fa-angle-right fa-stack-1x arrow" : "fas fa-angle-left fa-stack-1x arrow"}></i>
                      </span>
                    </button>
                  </div>
                  <div className={collapsedPane === 'left-Pane' ? "text-wrapper-toggle-title vertical-title" : "text-wrapper-toggle-title"}>{leftPaneTitle}</div>
                </div>
              </div>
              <div
                className="left-content2"
                style={{ display: collapsedPane === 'left-Pane' ? 'none' : 'block' }}
              >
                {leftPaneContent}
              </div>
              {collapsedPane === 'left-Pane' && (
                <div className="collapsed-overlay" onClick={collapseLeftPane} style={{ width: `${COLLAPSED_SIZE}px`, minWidth: `${COLLAPSED_SIZE}px`, position: 'absolute', left: 0, top: 0, bottom: 0, zIndex: 2, background: 'transparent', visibility: leftPaneVisibility ? 'visible' : 'hidden' }}>
                  {/* Collapsed left pane UI */}
                  <div className="frame-viewheader collapsed">
                    <div className="btn-label">
                      <div className="expand-collapse">
                        <button className="icon-button" style={{ visibility: rightPaneVisibility ? 'visible' : 'hidden' }}
                          onClick={(e) => { e.stopPropagation(); collapseLeftPane(); }}
                        >
                          <span className="fa-stack custom-icon">
                            <i className="fas fa-circle fa-stack-2x circle"></i>
                            <i className="fas fa-angle-right fa-stack-1x arrow"></i>
                          </span>
                        </button>
                      </div>
                      <div className="text-wrapper-toggle-title vertical-title">{leftPaneTitle}</div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </Allotment.Pane>

          {/* Right Pane — always mounted, content is never unmounted */}
          <Allotment.Pane minSize={COLLAPSED_SIZE}>
            <div className="flex-main-frame" style={{ visibility: rightPaneVisibility ? 'visible' : 'hidden' }}>
              <div className={collapsedPane === 'right-Pane' ? "frame-viewheader collapsed" : "frame-viewheader"}>
                <div className="btn-label">
                  <div className="expand-collapse">
                    <button className="icon-button" style={{ visibility: leftPaneVisibility ? 'visible' : 'hidden' }}
                      onClick={collapsedPane === 'right-Pane' ? (e) => { e.stopPropagation(); collapseRightPane(); } : () => collapseRightPane()}
                    >
                      <span className="fa-stack custom-icon">
                        <i className="fas fa-circle fa-stack-2x circle"></i>
                        <i className={collapsedPane === 'right-Pane' ? "fas fa-angle-left fa-stack-1x arrow" : "fas fa-angle-right fa-stack-1x arrow"}></i>
                      </span>
                    </button>
                  </div>
                  <div className={collapsedPane === 'right-Pane' ? "text-wrapper-toggle-title vertical-title-right" : "text-wrapper-toggle-title"}>{rightPaneTitle}</div>
                </div>
              </div>
              <div
                className="right-content2"
                style={{ display: collapsedPane === 'right-Pane' ? 'none' : 'block' }}
              >
                {rightPaneContent}
              </div>
              {collapsedPane === 'right-Pane' && (
                <div className="collapsed-overlay" onClick={collapseRightPane} style={{ width: `${COLLAPSED_SIZE}px`, minWidth: `${COLLAPSED_SIZE}px`, position: 'absolute', right: 0, top: 0, bottom: 0, zIndex: 2, background: 'transparent', visibility: rightPaneVisibility ? 'visible' : 'hidden' }}>
                  {/* Collapsed right pane UI */}
                  <div className="frame-viewheader collapsed">
                    <div className="btn-label">
                      <div className="expand-collapse">
                        <button className="icon-button" style={{ visibility: leftPaneVisibility ? 'visible' : 'hidden' }}
                          onClick={(e) => { e.stopPropagation(); collapseRightPane(); }}
                        >
                          <span className="fa-stack custom-icon">
                            <i className="fas fa-circle fa-stack-2x circle"></i>
                            <i className="fas fa-angle-left fa-stack-1x arrow"></i>
                          </span>
                        </button>
                      </div>
                      <div className="text-wrapper-toggle-title vertical-title-right">{rightPaneTitle}</div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </Allotment.Pane>
        </Allotment>
      </div>
    </div>
  );
};

export default ToggleSplitPane;

